import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  PROPOSAL_BUNDLE_STATE,
  findHotelItemById,
  filterHotelItems,
  hasProposalItemHotel,
  isHotelProposalBundle,
  type HotelProposalBundle,
  type HotelSegmentGroup,
  type ProposalBundle,
  type ProposalItem
} from '../../../types/proposalBundles';
import type {
  OnProposalBundleClose,
  OnProposalBundleFeedbackUpdate,
  OnProposalItemSelect
} from '../../../types/proposalHandlers';
import { formatHotelLocationInfo } from '../../../utils/routeInfoFormatter';
import { applyOptimisticReject } from '../utils';
import ProposalBundleFeedback from '../ProposalBundleFeedback';
import { fetchHotelProposalReasons } from '../../../api/hotelProposalReasons';
import type {
  HotelProposalReasonsLoadStatus,
  HotelProposalReasonsResult
} from '../../../types/hotelProposalReasons';
import HotelDetailModal from './HotelDetailModal';
import PartitionedHotelCard from './PartitionedHotelCard';
import SingleHotelCard from './SingleHotelCard';

const isPartitionedHotelBundle = (bundle: ProposalBundle): boolean =>
  isHotelProposalBundle(bundle) &&
  bundle.partition.partitioned === true &&
  (bundle.partition.segmentCount ?? 0) > 1;

const groupHotelItemsBySegment = (items: ProposalItem[]): HotelSegmentGroup[] => {
  const map = new Map<number, HotelSegmentGroup>();

  items.filter(hasProposalItemHotel).forEach(item => {
    const segmentIndex = item.hotel.segment.index ?? 0;
    const existing = map.get(segmentIndex);
    if (existing) {
      existing.items.push(item);
      return;
    }

    map.set(segmentIndex, {
      index: segmentIndex,
      checkin: item.hotel.segment.checkin ?? null,
      checkout: item.hotel.segment.checkout ?? null,
      items: [item]
    });
  });

  return Array.from(map.values()).sort((left, right) => left.index - right.index);
};

const resolvePartitionedSegmentCount = (bundle: HotelProposalBundle): number =>
  bundle.partition.segmentCount ?? groupHotelItemsBySegment(filterHotelItems(bundle.items)).length;

const countPartitionedSegmentProgress = (
  bundle: HotelProposalBundle,
  items: HotelProposalBundle['items'] = bundle.items
): { selected: number; total: number } => {
  if (!isPartitionedHotelBundle(bundle)) {
    return { selected: 0, total: 0 };
  }

  const total = resolvePartitionedSegmentCount(bundle);
  const selected = groupHotelItemsBySegment(items).filter(group => group.items.some(item => item.selected)).length;

  return { selected, total };
};

export const applyOptimisticHotelSelect = (bundle: HotelProposalBundle, itemId: number): HotelProposalBundle => {
  const targetItem = findHotelItemById(bundle.items, itemId);
  if (!targetItem) {
    return bundle;
  }

  const targetSegmentIndex = targetItem.hotel.segment.index;
  const isPartitioned = isPartitionedHotelBundle(bundle);

  const items = bundle.items.map(item => {
    if (!hasProposalItemHotel(item)) {
      return item;
    }

    if (isPartitioned) {
      if (item.hotel.segment.index !== targetSegmentIndex) {
        return item;
      }
      return { ...item, selected: item.id === itemId };
    }

    return { ...item, selected: item.id === itemId };
  });

  if (!isPartitioned) {
    return { ...bundle, state: 'selected', items };
  }

  const total = resolvePartitionedSegmentCount(bundle);
  const selected = countPartitionedSegmentProgress(bundle, items).selected;
  const state = total > 0 && selected === total ? 'selected' : 'suggesting';

  return {
    ...bundle,
    state,
    items,
    partition: {
      ...bundle.partition,
      allSegmentsSelected: total > 0 && selected === total
    }
  };
};

const useHotelProposalReasons = (tripId: number, bundle: HotelProposalBundle) => {
  const requestKey = `${tripId}:${bundle.id}:${bundle.items.length}`;
  const [status, setStatus] = useState<HotelProposalReasonsLoadStatus>(
    bundle.items.length === 0 ? 'idle' : 'loading'
  );
  const [result, setResult] = useState<HotelProposalReasonsResult | null>(null);
  const [loadedKey, setLoadedKey] = useState(requestKey);

  if (loadedKey !== requestKey) {
    setLoadedKey(requestKey);
    setResult(null);
    setStatus(bundle.items.length === 0 ? 'idle' : 'loading');
  }

  useEffect(() => {
    if (bundle.items.length === 0) return undefined;

    let cancelled = false;

    fetchHotelProposalReasons(String(tripId), bundle.id)
      .then(data => {
        if (cancelled) return;
        setResult(data);
        setStatus(data.status === 'completed' ? 'completed' : 'unavailable');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [tripId, bundle.id, bundle.items.length]);

  return { status, result };
};

interface Props {
  bundle: HotelProposalBundle;
  productNumber: number;
  includeTax: boolean;
  onSelect?: OnProposalItemSelect;
  onClose?: OnProposalBundleClose;
  onFeedbackUpdate?: OnProposalBundleFeedbackUpdate;
  disabled?: boolean;
  tripId: number;
  isSpLayout?: boolean;
}

const HotelCard = ({
  bundle,
  productNumber,
  includeTax,
  onSelect,
  onClose,
  onFeedbackUpdate,
  disabled = false,
  tripId,
  isSpLayout = false
}: Props) => {
  const { theme } = useAITTheme();
  const isRejected = bundle.state === PROPOSAL_BUNDLE_STATE.REJECTED;
  const styles = useMemo(() => getStyles(theme, isRejected), [theme, isRejected]);
  const locationInfo = useMemo(
    () => formatHotelLocationInfo(bundle.route.destination, bundle.route.departureDate, bundle.route.returnDate),
    [bundle.route.destination, bundle.route.departureDate, bundle.route.returnDate]
  );
  const [detailModalItemId, setDetailModalItemId] = useState<number | null>(null);
  const proposalReasons = useHotelProposalReasons(tripId, bundle);

  const handleSelect = (itemId: number) => {
    onSelect?.(bundle.id, itemId, current =>
      isHotelProposalBundle(current) ? applyOptimisticHotelSelect(current, itemId) : current
    );
  };

  const handleClose = () => {
    onClose?.(bundle.id, applyOptimisticReject(bundle));
  };

  const handleOpenDetail = (itemId: number) => {
    setDetailModalItemId(itemId);
  };

  const handleCloseDetail = () => {
    setDetailModalItemId(null);
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerTitleGroup}>
          <span style={styles.productBadge}>商品{productNumber}</span>
          <span style={styles.categoryLabel}>🏨 宿泊施設{locationInfo}</span>
          {isRejected && <span style={styles.rejectedLabel}>予約しない</span>}
        </div>
        <div style={styles.headerActions}>
          {onFeedbackUpdate && (
            <ProposalBundleFeedback
              bundleId={bundle.id}
              tripId={tripId}
              currentFeedback={bundle.feedback}
              commentSubmitted={bundle.feedbackCommentSubmitted}
              onFeedbackUpdate={onFeedbackUpdate}
            />
          )}
          {!isRejected && (
            <button
              type="button"
              style={{
                ...styles.closeButton,
                ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
              }}
              onClick={handleClose}
              disabled={disabled}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {proposalReasons.status === 'error' && (
        <Alert variant="danger" style={{ marginBottom: '16px' }}>
          提案理由の取得に失敗しました。
        </Alert>
      )}

      {isPartitionedHotelBundle(bundle) ? (
        <PartitionedHotelCard
          bundle={bundle}
          includeTax={includeTax}
          disabled={disabled}
          isSpLayout={isSpLayout}
          onSelect={handleSelect}
          onOpenDetail={handleOpenDetail}
          reasons={proposalReasons}
        />
      ) : (
        <SingleHotelCard
          bundle={bundle}
          includeTax={includeTax}
          disabled={disabled}
          isSpLayout={isSpLayout}
          onSelect={handleSelect}
          onOpenDetail={handleOpenDetail}
          reasons={proposalReasons}
        />
      )}

      {detailModalItemId !== null && (
        <HotelDetailModal
          open={detailModalItemId !== null}
          onClose={handleCloseDetail}
          tripId={tripId}
          itemId={detailModalItemId}
        />
      )}
    </div>
  );
};

export default HotelCard;

const getStyles = (theme: AITTheme, isRejected: boolean) => ({
  card: {
    backgroundColor: theme.whiteColor,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: `2px solid ${isRejected ? theme.grayBorderColor : theme.linkColor}`,
    position: 'relative' as const,
    opacity: isRejected ? 0.6 : 1
  },
  closeButton: {
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    fontWeight: 300 as const,
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  header: {
    fontWeight: 700 as const,
    color: isRejected ? theme.grayTextColor : theme.linkColor,
    marginBottom: '16px',
    fontSize: '17px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  headerTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    flex: 1
  },
  productBadge: {
    backgroundColor: isRejected ? theme.grayTextColor : theme.linkColor,
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  categoryLabel: {
    fontSize: '14px',
    textDecoration: isRejected ? 'line-through' : 'none'
  },
  rejectedLabel: {
    backgroundColor: '#f5f5f5',
    color: theme.grayTextColor,
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const,
    marginLeft: 'auto'
  }
});
